import { useTranslation } from 'react-i18next'
import { Helmet } from 'react-helmet-async'
import { Outlet, useNavigate, useSearchParams, createSearchParams, Link } from "../navigation";
import { Popover, PopoverButton, PopoverPanel } from '@headlessui/react'
import { Formik, Form, Field } from "formik"
import { ChevronDownIcon } from '@heroicons/react/20/solid'
import clsx from 'clsx'
import _, { Dictionary } from 'lodash'

import { IconEdit } from "../components/icons";
import DeleteButton from "../components/DeleteButton";
import { useFxContext } from '../forecast/FxProvider'
import { ReferenceLinker } from '../components/input/ReferenceLinker'
import { getReference, isEquivalent } from '../components/input/references'
import { CostLink, useCosts } from './CostsProvider'
import Modal from '../components/Modal'
import { useSingleTeamMangement } from '../Management'
import { DatePicker } from '../components/input/DatePicker'
import { design_registration, family_member, trade_mark } from '../data'
import { CostFormFields, CostFormFooter } from './CostFormFields'
import { useLocalState } from '../settings/localStorage'
import { Family, Member } from '../patents/patents'
import { CostItem } from './CostsProvider'
import { emptyStringAsUndefined } from '../utils/strings'
import { exportCosts as exportCostsAction, AccumulationConfig, accumulateCosts } from './utils'
import { SuggestedInput } from '../components/input/SuggestedInput'
import { CostItemDetails } from './CostsTable'
import { XMarkIcon } from '@heroicons/react/20/solid'
import ToggleButton from '../components/ToggleButton'
import { useFilteredPatents } from '../filter/FilteredPatents'
import { CostsGraph, CostsMainDriversGraph } from './CostsGraph'
import { SortButton } from '../patents/datawizard/DataWizard'
import { usePatents } from '../patents/PatentsProvider'
import { useRoles } from '../user/Auth'
import { Trademark, TrademarkFamily, useTrademarks } from '../trademarks/TrademarksProvider'
import { useFilteredTrademarks } from '../filter/FilteredTrademarks'
import { DesignFamily, DesignRegistration, useDesigns } from '../designs/DesignsProvider'
import { useFilteredDesigns } from '../filter/FilteredDesigns'
import { useExternal } from '../external'

const groupBysByEntity: Record<string, string[]> = {
    [family_member]: [
        'invoice-number',
        'cost-center',
        'patentFamilyReference',
        'familyMemberReference',
        'country',
    ],
    [trade_mark]: [
        'invoice-number',
        'cost-center',
        'trademark',
        'family',
        'country',
    ],
    [design_registration]: [
        'invoice-number',
        'cost-center',
        'design',
        'designFamily',
        'country',
    ]
}

interface CostRowProps {
    reference: string;
    total: number;
    byYear: Record<number, number>;
    costIds: Dictionary<number[]>;
}

function byInvoice(costs: CostItem[], linkByCostId: Record<number, CostLink>, entity: string, activeEntityIds: Set<number>, config: AccumulationConfig): CostRowProps[] {
    return _(costs)
        .groupBy(c => c.reference)
        .mapValues((costs: CostItem[], reference) => {
            const items = costs.filter(c => {
                const link = linkByCostId[c.costId]
                return link !== undefined && link.entity === entity && activeEntityIds.has(link.entityId)
            })
            const result = items.length > 0 ? {
                reference,
                ...accumulateCosts(items, config)
            } : null
            //console.log({costs, result})
            return result
        })
        .values().filter(Boolean).value()
}

function byCostCenter(costs: CostItem[], linkByCostId: Record<number, CostLink>, entity: string, activeEntityIds: Set<number>, config: AccumulationConfig): CostRowProps[] {
    return _(costs)
        .groupBy(c => c.costCenter ?? '-')
        .mapValues((costs: CostItem[], reference) => {
            const items = costs.filter(c => {
                const link = linkByCostId[c.costId]
                return link !== undefined && link.entity === entity && activeEntityIds.has(link.entityId)
            })
            return items.length > 0 ? {
                reference,
                ...accumulateCosts(items, config)
            }: null
        })
        .values().filter(Boolean).value()
}

function byPatentFamily(families: Family[], membersByFamilyId: Record<number, Member[]>, costsPerMemberId: Record<number, CostItem[]>, config: AccumulationConfig): CostRowProps[] {
    return families.map(f => {
        const members = membersByFamilyId[f.patentFamilyId] ?? []
        const costs = members.flatMap(m => costsPerMemberId[m.familyMemberId] ?? [])
        return {
            reference: f.internalReference,
            ...accumulateCosts(costs, config),
        }
    })
}

function byPatentMember(members: Member[], costsPerMemberId: Record<number, CostItem[]>, config: AccumulationConfig): CostRowProps[] {
    return members.map(m => {
        const costs = costsPerMemberId[m.familyMemberId] ?? []
        return {
            reference: m.internalReference,
            ...accumulateCosts(costs, config),
        }
    })
}

function byTmFamily(families: TrademarkFamily[], trademarksByFamilyId: Record<number, Trademark[]>, costsPerTrademarkId: Record<number, CostItem[]>, config: AccumulationConfig): CostRowProps[] {
    return families.map(f => {
        const trademarks = trademarksByFamilyId[f.familyId] ?? []
        const costs = trademarks.flatMap(m => costsPerTrademarkId[m.trademarkId] ?? [])
        return {
            reference: f.reference,
            ...accumulateCosts(costs, config),
        }
    })
}

function byTmMember(trademarks: Trademark[], costsPerTrademarkId: Record<number, CostItem[]>, config: AccumulationConfig): CostRowProps[] {
    return trademarks.map(m => {
        const costs = costsPerTrademarkId[m.trademarkId] ?? []
        return {
            reference: m.reference,
            ...accumulateCosts(costs, config),
        }
    })
}

function byDesignFamily(families: DesignFamily[], registrationsByFamilyId: Record<number, DesignRegistration[]>, costsPerRegistrationId: Record<number, CostItem[]>, config: AccumulationConfig): CostRowProps[] {
    return families.map(f => {
        const regs = registrationsByFamilyId[f.designFamilyId] ?? []
        const costs = regs.flatMap(r => costsPerRegistrationId[r.registrationId] ?? [])
        return {
            reference: f.reference,
            ...accumulateCosts(costs, config),
        }
    })
}

function byDesignRegistration(registrations: DesignRegistration[], costsPerRegistrationId: Record<number, CostItem[]>, config: AccumulationConfig): CostRowProps[] {
    return registrations.map(r => {
        const costs = costsPerRegistrationId[r.registrationId] ?? []
        return {
            reference: r.reference,
            ...accumulateCosts(costs, config),
        }
    })
}

function byCountry(ipRights: {countryCode: string, entityId: number}[], costsPerEntityId: Record<number, CostItem[]>, config: AccumulationConfig): CostRowProps[] {
    return _(ipRights)
        .groupBy(m => m.countryCode)
        .mapValues((rights, countryCode) => {
            const costs = rights.flatMap(({entityId}) => costsPerEntityId[entityId] ?? [])
            return {
                reference: countryCode,
                ...accumulateCosts(costs, config),
            }
        })
        .values()
        .value()
}


export function Costs({entity}: {entity: string}) {
    const {t} = useTranslation()

    const groupBys = groupBysByEntity[entity] ?? []

    const {team} = useSingleTeamMangement()
    const {isEditUser} = useRoles()
    const currency = team?.currency ?? 'EUR'
    const {fxConverter} = useFxContext()
    const {families, members, membersByFamilyId, memberById} = useFilteredPatents()
    const {trademarks, trademarkFamilies, trademarksByFamilyId} = useFilteredTrademarks()
    const {designRegistrations, designFamilies, registrationsByFamilyId: designRegsByFamilyId} = useFilteredDesigns()
    const {costsByMemberId, costsByEntity, costs, linkByCostId} = useCosts()

    const [inclVat, setInclVat] = useLocalState(entity + '.costs-incl-vat', false)
    const [groupBy, setGroupBy] = useLocalState(entity + '.costs-group-by', groupBys[0])
    const [sortBy, setSortBy] = useLocalState(entity + '.costs-sort-by', 'reference')
    const [sortOrder, setSortOrder] = useLocalState(entity + '.costs-sort-order', 1)

    const config = {fxConverter, currency, inclVat, entity}

    // make dependent on entity
    const activeEntityIds
        = entity === family_member
        ? new Set(members.map(m => m.familyMemberId))
        : entity === design_registration
        ? new Set(designRegistrations.map(r => r.registrationId))
        : new Set(trademarks.map(t => t.trademarkId))
    const ipRights
        = entity === family_member
        ? members.map(m => ({...m, entityId: m.familyMemberId}))
        : entity === design_registration
        ? designRegistrations.map(r => ({...r, entityId: r.registrationId}))
        : trademarks.map(t => ({...t, entityId: t.trademarkId}))

    const rows: CostRowProps[] 
        = groupBy === 'invoice-number'
        ? byInvoice(costs, linkByCostId, entity, activeEntityIds, config)
        : groupBy === 'cost-center'
        ? byCostCenter(costs, linkByCostId, entity, activeEntityIds, config)
        : groupBy === 'patentFamilyReference'
        ? byPatentFamily(families, membersByFamilyId, costsByMemberId, config)
        : groupBy === 'familyMemberReference'
        ? byPatentMember(members, costsByMemberId, config)
        : groupBy === 'trademark'
        ? byTmMember(trademarks, costsByEntity[entity] ?? {}, config)
        : groupBy === 'family'
        ? byTmFamily(trademarkFamilies, trademarksByFamilyId, costsByEntity[entity] ?? {}, config)
        : groupBy === 'design'
        ? byDesignRegistration(designRegistrations, costsByEntity[entity] ?? {}, config)
        : groupBy === 'designFamily'
        ? byDesignFamily(designFamilies, designRegsByFamilyId, costsByEntity[entity] ?? {}, config)
        : byCountry(ipRights, costsByEntity[entity] ?? {}, config)

    const sortedRows = _(rows)
        .filter(r => r.total > 0)
        .sortBy(r => _.get(r, sortBy) ?? 0)
        .thru(rs => sortOrder === 1 ? rs : rs.reverse())
        .value()

    //console.log({sortedRows, sortBy})
    // TODO: should we make sure that we also "fill holes" in the years?
    const years = _(rows).flatMap(r => Object.keys(r.byYear)).uniq().sortBy().reverse().value()

    const format = new Intl.NumberFormat(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})

    const total = {
        total: _.sumBy(sortedRows, r => r.total),
        byYear: _.fromPairs(years.map(y => [y, _.sumBy(sortedRows, r => r.byYear[y] ?? 0)]))
    }
    
    const totalRow = (
        <tr className="border-b-2 border-pcx-300">
            <td className="py-2 px-3 text-pcx-600 font-semibold">{t('total')}</td>
            <td className="py-2 px-3 text-right font-semibold">{format.format(total.total)}</td>
            {years.map((y, i) =>
                <td key={i} className="py-2 px-3 text-right font-semibold">{format.format(total.byYear[y])}</td>
            )}
        </tr>
    )

    function exportCosts() {
        exportCostsAction(costs, linkByCostId, memberById, team.displayName ?? team.realm)
    }

    const mainDrivers = _(rows).map(r => ({name: r.reference, total: r.total})).sortBy('total').reverse().slice(0, 10).value()

    const addUrl = {pathname: 'cost', search: createSearchParams({entity, limitedIds: [...activeEntityIds].join(',')}).toString()}

    return <>
        {/* @ts-ignore */}
        <Helmet>
            <title>{t('costs')} | Patent Cockpit</title>
        </Helmet>
        <div className={"portfolio-menu max-w-2xl items-baseline"}>
            <h2 className="modern-h2">
                {t('costs')}
            </h2>
            <span className='text-slate-500 text-sm mr-auto'>({t('in-currency', { currency })})</span>
        </div>
        <div className="main-content bg-pcx-100 pt-2">

            <div className='w-fit'>
                <div className='flex flex-col-reverse sm:flex-row gap-2 items-end sm:items-center justify-between max-w-3xl'>
                    <label>
                        <span className='mr-2 text-pcx-600'>{t('group-by')}:</span>
                        <select className='form-select py-px bg-transparent text-pcx-600' value={groupBy} onChange={e => setGroupBy(e.target.value)}>
                            {groupBys.map(g => <option key={g} value={g}>{t(g)}</option>)}
                        </select>
                    </label>
                    {/* TODO Make import independent of entity */}
                    <CostActionMenu {...{ inclVat, setInclVat, exportCosts, addUrl, withExcel: entity === family_member }} />
                </div>

                {sortedRows.length > 0 ?
                    <div className='py-4 max-w-5xl flex flex-col md:flex-row gap-8'>
                        <div className='min-w-xs max-w-xs p-4 bg-white rounded-lg shadow' key={_.size(total.byYear)}>
                            <CostsGraph {...total} />
                        </div>
                        <div className='min-w-xs max-w-xs p-4 bg-white rounded-lg shadow' key={_.size(mainDrivers)}>
                            <CostsMainDriversGraph data={mainDrivers} />
                        </div>
                    </div>
                    : <div className='p-4 bg-white rounded-md shadow w-fit my-4'>
                            <h3>{t('no-costs')}</h3>
                            <p className='mt-2 mb-4 text-slate-600'>{t('add-cost-item')}</p>
                            {isEditUser && <Link to={addUrl} className="btn-secondary h-fit">{t('add-cost')} </Link>}
                        </div>
                    }
            </div> 
            <div className='flex flex-row gap-4 items-start'>
                {sortedRows.length > 0 &&
                    <div className='p-4 bg-white rounded-md shadow w-fit'>

                        <table className='tabular-nums'>
                            <thead>
                                <tr className="border-b-2 border-pcx-300">
                                    {[
                                        { label: t(groupBy), fieldName: 'reference' },
                                        { label: t('total'), fieldName: 'total' },
                                        ...(years.map(y => ({ label: y, fieldName: `byYear.${y}` })))
                                    ].map(({ label, fieldName }, i) =>
                                        <th key={i}
                                            className={clsx(
                                                "md:pl-3 pr-3 pt-3 pb-3 text-pcx-600 text-sm font-semibold uppercase tracking-wider whitespace-nowrap",
                                                i > 0 ? 'text-right' : 'text-left',
                                            )}
                                        >
                                            {label} <SortButton {...{ searchField: fieldName, sortField: sortBy, setSortField: setSortBy, sortOrder, setSortOrder }} />
                                        </th>)}
                                </tr>
                            </thead>
                            <tbody>
                                {totalRow}
                                {sortedRows.map((r, i) =>
                                    <tr key={i} className="border-b-2 border-pcx-200 last:border-pcx-300">
                                        <td className="py-2 px-3">
                                            <DetailsLink className="text-pcx-600 hover:text-pcx-800 font-medium" {...{ costIds: r.costIds.total }}>
                                                {r.reference}
                                            </DetailsLink>
                                        </td>
                                        <td className="py-2 px-3 text-right">
                                            <DetailsLink {...{ costIds: r.costIds.total }}>
                                                {format.format(r.total)}
                                            </DetailsLink>
                                        </td>
                                        {years.map((y, i) =>
                                            <td key={i} className="py-2 px-3 text-right">
                                                <DetailsLink {...{ costIds: r.costIds[y] }} >
                                                    {format.format(r.byYear[y])}
                                                </DetailsLink>
                                            </td>
                                        )}
                                    </tr>
                                )}
                            </tbody>
                            <tfoot>
                                {totalRow}
                            </tfoot>
                        </table>

                    </div>}
                <Outlet />
            </div>
        </div>
    </>
}

function DetailsLink({costIds, className, children}: {children: React.ReactNode, className?: string, costIds: number[]}) {
    if ((costIds ?? []).length === 0)
        return <span>-</span>
    else
        return (
            <Link
                to={{ pathname: 'details', search: `?costId=${costIds.join(',')}` }}
                className={className ?? "hover:text-pcx-600"}
            >
                {children}
            </Link>
        )
}

// TODO support excel import/export also with TMs
function CostActionMenu({inclVat, setInclVat, exportCosts, addUrl, withExcel = false}) {
    const {t} = useTranslation()
    const {isEditUser} = useRoles()

    const urls = isEditUser ? [
        {label: 'add-cost', url: addUrl},
        withExcel && {label: 'excel-import', url: 'import'},
    ].filter(Boolean) : []

    const itemStyle = 'hover:bg-pcx-100 font-normal whitespace-nowrap block px-3 py-1 text-sm text-gray-700 hover:text-pcx-700'

    return (
        <Popover as="div" className="relative">
            <PopoverButton className="btn-secondary border-slate-400 text-pcx-600 py-px pl-3 text-base rounded inline-flex justify-center gap-1 font-normal focus:outline-0 focus:ring-0">
                {t('forecast.actions')}
                <ChevronDownIcon className='h-5 w-5 mt-0.5' aria-hidden="true" />
            </PopoverButton>
            <PopoverPanel className="absolute z-10 right-0 mt-2 rounded-sm shadow-lg overflow-hidden bg-white border border-pcx-500 ring-1 ring-pcx-600 ring-opacity-5 focus:outline-none">
                {urls.map(({label, url}) =>
                        <Link key={label} to={url} className={itemStyle} >
                            {t(label)}
                        </Link>
                )}
                {withExcel && <button className={clsx(itemStyle, 'w-full text-left')} type="button" onClick={exportCosts}>
                    {t('excel-export')}
                </button>}
                <label className={clsx(itemStyle, 'inline-flex gap-2')}>
                    {t('include-vat')} <ToggleButton checked={inclVat} setChecked={setInclVat} />
                </label>
            </PopoverPanel>
        </Popover>
    )
}

const format = new Intl.NumberFormat(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})

export function CostItemsDetails() {
    const {t} = useTranslation()
    const {isEditUser} = useRoles()

    const [searchParams] = useSearchParams()
    const {costById, linkByCostId} = useCosts()
    const { memberById } = usePatents()
    const { trademarkById } = useTrademarks()
    const { familyById: designFamilyById, registrationById: designRegistrationById } = useDesigns()
    const { memberById: competitorMemberById } = useExternal()

    const costIds = (searchParams.get('costId') ?? '').split(',').map(id => parseInt(id))

    const details = _(costIds)
        .map(costId => {
            const cost = costById[costId]
            const link = linkByCostId[costId]

            if (cost === undefined || link === undefined) return null

            const ref = getReference(link, {}, memberById, trademarkById, {}, designFamilyById, designRegistrationById, competitorMemberById)

            if (ref?.internalReference === undefined) return null

            return { cost, ...ref, link }
        })
        .filter(Boolean)
        .sortBy(({ cost }) => cost.date)
        .value()

    if (details.length === 0) return null

    return (
        <div className='p-4 bg-white rounded-lg shadow flex flex-col min-w-xs'>
            <div className='flex flex-row gap-4 justify-between px-2'>
                <h4 className='mb-4'>{t('details')}</h4>
                <Link to=".." className='h-6 w-6 text-pcx-700'><XMarkIcon /></Link>
            </div>
            {details.map(({ cost, to, internalReference, link }) =>
                <div key={cost.costId} className='p-2 border-t-2 last:border-b-2 border-pcx-300'>
                    <div className='flex flex-row gap-1 mb-2'>
                        <Link to={to} className='text-pcx-600 hover:text-pcx-800 font-medium mr-auto'>{internalReference}</Link>
                        {isEditUser && <Actions {...cost} entity={link.entity} />}
                    </div>
                    <CostItemDetails {...{ cost, format }} />
                </div>
            )}
        </div>
    )
}

function Actions({entity, ...cost}: CostItem & {entity: string}) {
    const {deleteCost} = useCosts()
    return <>
        <Link
            to={{ 
                pathname: '../cost',
                search: `?${createSearchParams({entity, costId: '' + cost.costId })}` 
            }} 
            className="btn-secondary w-5 h-5 p-px"
        >
            <IconEdit />
        </Link>
        <DeleteButton
            className="w-5 h-5 p-px btn-warn-secondary"
            deleteAction={() => deleteCost(cost)} />
    </>
}


// TODO: should we allow no linking? I don't think so
export function PostCost() {
    const {t} = useTranslation()
    const navigate = useNavigate()

    const {team} = useSingleTeamMangement()
    const currency = team?.currency ?? 'EUR'

    const {familyById, memberById} = usePatents()
    const {trademarkById} = useTrademarks()
    const { familyById: designFamilyById, registrationById: designRegistrationById } = useDesigns()
    const { memberById: competitorMemberById } = useExternal()
    const {costs, costById, linkByCostId, postCost, deleteCostLink, postCostLink} = useCosts()

    const [searchParams] = useSearchParams()

    const costId = searchParams.get('costId')
    const entity = searchParams.get('entity')
    const _limitedIds = searchParams.get('limitedIds')
    //const entityId = parseInt(searchParams.get('entityId'))

    const limitedIds = (entity && _limitedIds) ? {[entity]: _limitedIds?.split(',').map(id => parseInt(id))} : {}

    const isEdit = costId in costById

    // TODO: validate - Patent Ref, Betrag, Datum, Rechnungsnummer  non empty
    const defaultLink = getReference({entity, entityId: limitedIds[entity]?.[0]}, familyById, memberById, trademarkById, {}, designFamilyById, designRegistrationById, competitorMemberById)
    const initialValues = isEdit
        ? {
            costCenter: '',
            ...costById[costId],
            link: linkByCostId[costId] ? getReference(linkByCostId[costId], familyById, memberById, trademarkById, {}, designFamilyById, designRegistrationById, competitorMemberById) : defaultLink
        } : {
            date: new Date().toISOString().slice(0, 10),
            amount: 0,
            currency,
            vat: 0,
            reference: '',
            comment: '',
            costCenter: '',
            link: defaultLink,
        }

    const costCenters = _(costs).map(c => c.costCenter).filter(Boolean).uniq().sortBy().value()

    return (
        <Modal>
            <Formik
                initialValues={initialValues}
                enableReinitialize
                onSubmit={(values: CostItem & {link: CostLink}) => {
                    const clean = {...values, costCenter: emptyStringAsUndefined(values.costCenter)}
                    //console.log({clean})
                    postCost(clean).then(async ({costId}) => {
                        if (!isEdit) {
                            //console.log({costId, ...values.link, operation: 'add'})
                            await postCostLink({costId, ...values.link})
                        } else if (!isEquivalent(values.link, initialValues.link)) {
                            if (initialValues.link) {
                                // delete old link
                                //console.log({...initialValues.link, operation: 'delete'})
                                await deleteCostLink({...initialValues.link, costId})
                            }
                            //console.log({...values.link, costId, operation: 'add'})
                            await postCostLink({costId, ...values.link})
                        }
                        navigate('..')
                    })
                }}
                validate={(values) => {
                    const errors = {}
                    if (emptyStringAsUndefined(values.link.internalReference) === undefined) {
                        errors['link'] = t('required')
                    }
                    return errors
                }}
            >{({values, isSubmitting, touched, errors}) =>
                <Form>
                    <CostFormFields values={values} isEdit={isEdit} topFields={<>
                        <div className='sm:col-span-2 flex flex-col sm:flex-row gap-4'>
                            <div className='grow'>
                                <label htmlFor="link" className='label mb-1'>
                                    {t('reference')} {
                                        // @ts-ignore
                                        errors.link && touched.link && <span className='text-warn-600'>{errors.link}</span>
                                    }
                                </label>
                                {!isSubmitting &&
                                    // don't render when submitting as the reference changes...
                                    <Field id="link" name="link" {...{ limitedIds, disallowNoLink: true }} as={ReferenceLinker} supportedEntities={[entity]} />}
                            </div>
                            <div>
                                <label htmlFor="date" className='label mb-1'>{t('date')}</label>
                                <Field id="date" name="date" required className="form-input" as={DatePicker} />
                            </div>
                        </div>
                        <div>
                            <label htmlFor="reference" className='label mb-1'>{t('invoice-number')}</label>
                            <Field id="reference" name="reference" required className="form-input" />
                        </div>
                        <div>
                            <label htmlFor="costCenter" className='label mb-1'>{t('cost-center')}</label>
                            {!isSubmitting &&
                                // don't render when submitting as the reference changes...
                                <Field id="costCenter" name="costCenter" {...{ className: "form-input", suggestions: costCenters }} as={SuggestedInput} />}
                        </div>
                    </>} />
                    <CostFormFooter cancel={<Link to='..' className="btn-secondary">{t('cancel')}</Link>} />
                </Form>
            }</Formik>
        </Modal>
    )
}

